import type { IRequestProps } from '@experiences/interfaces';
import { getUserLanguageFromLocalStorage } from '@experiences/locales';
import { resolveHostUsingEnvironmentInfo } from '@experiences/util';
import urljoin from 'url-join';

import {
    axiosDelete,
    get,
    post,
} from '../utility/Requests';

const hypervisorBasePath = '/hypervisor_';

export const getHypervisorBaseRoute = (accountLogicalName: string, tenantId: string) => {
    if (window.location.origin.includes('localhost')) {
        return urljoin('/', accountLogicalName, tenantId, hypervisorBasePath);
    }

    const resolvedHost = resolveHostUsingEnvironmentInfo(window.location.host);
    return urljoin(`https://${resolvedHost}`, accountLogicalName, tenantId, hypervisorBasePath);
};

export const getHypervisorODataRoute = (accountLogicalName: string, tenantId: string) =>
    urljoin(getHypervisorBaseRoute(accountLogicalName, tenantId), 'odata');

// Thin wrappers around the shared HTTP helpers that always set
// `ignoreBasePath: true`. Hypervisor URLs produced by `getHypervisorODataRoute`
// are already absolute (production) or root-relative (localhost, routed by the
// rsbuild dev proxy), so they must not have the portal's `/portal_/api` prefix
// stitched onto them. Using these ensures every hypervisor call bypasses the
// portal base path — callers can't accidentally forget the flag.
export const hypervisorGet = <T>(url: string, requestProps?: IRequestProps) =>
    get<T>(url, {
        ...requestProps,
        ignoreBasePath: true,
    });

export const hypervisorPost = <T>(url: string, requestProps?: IRequestProps) =>
    post<T>(url, {
        ...requestProps,
        ignoreBasePath: true,
    });

export const hypervisorDelete = (url: string, requestProps?: IRequestProps) =>
    axiosDelete(url, {
        ...requestProps,
        ignoreBasePath: true,
    });

/**
 * Builds a per-tenant hypervisor HTTP client. Binds the OData base URL and
 * standard tenant headers once so endpoint functions only need to declare the
 * relative path (and an optional body for POST) — eliminating the repetition
 * of `{ headers: getHeaders(tenantId) }` and `body: { request: ... }` across
 * every call site.
 *
 * POST bodies are automatically wrapped in `{ request: body }`, matching the
 * wire format the hypervisor OData actions expect. Call with no body for
 * action endpoints that take no payload (e.g. `Redeploy`, `RefreshConnection`).
 */
export const makeHypervisorClient = (accountLogicalName: string, selectedTenantId: string) => {
    const base = getHypervisorODataRoute(accountLogicalName, selectedTenantId);
    const headers = {
        'X-UiPath-Internal-TenantId': selectedTenantId,
        'X-UiPath-Internal-LanguageCode': getUserLanguageFromLocalStorage(),
        extendRequestHeaders: 'true',
    };
    return {
        get: <T>(path: string) => hypervisorGet<T>(base + path, { headers }),
        post: <T>(path: string, body?: unknown) => hypervisorPost<T>(base + path, {
            headers,
            ...(body !== undefined && { body: { request: body } }),
        }),
        delete: (path: string) => hypervisorDelete(base + path, { headers }),
    };
};
